import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { COMMUNITY_API_PREFIX } from 'shared/api/constants/api-constants'
import type { PaginationData } from 'shared/components/pagination'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseQueryClearOptions } from 'shared/types/tanstack-utility-types'
import { addQueryParam } from 'shared/utils/add-query-param'
import type { BannedMemberInterface } from 'modules/community/types/member-interface'
import { communityKeys } from './key-factory'

const BANNED_MEMBER_LIMIT = 9

interface UseBannedMembersOptions extends UseQueryClearOptions<
  PaginationData<BannedMemberInterface[]>,
  Error,
  PaginationData<BannedMemberInterface[]>
> {
  communityPath: string
  page: number
  searchBy: string
}

export const useBannedMembers = ({
  communityPath,
  page,
  searchBy,
  ...options
}: UseBannedMembersOptions): UseQueryResult<PaginationData<BannedMemberInterface[]>, Error> => {
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    PaginationData<BannedMemberInterface[]>
  >({
    method: RequestMethodsEnum.get,
  })

  return useQuery(
    queryOptions({
      queryKey: communityKeys.bannedMembers.list(communityPath, page, searchBy),
      queryFn: () => {
        const search = new URLSearchParams()
        addQueryParam(search, 'searchBy', searchBy)
        return fetcher(
          `${COMMUNITY_API_PREFIX}/${communityPath}/banned-users/${page}/${BANNED_MEMBER_LIMIT}?${search}`,
        )
      },
      enabled: Boolean(communityPath),
      ...options,
    }),
  )
}
